import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getGetReconciliationQueryKey,
  useDeleteRecording,
  useGetReconciliation,
  useRecordOccurrence,
} from '../api/generated/fluence'
import type { PendingOccurrence, SourceDrift } from '../api/generated/model'
import { formatDate, formatMoney, toMajor, toMinor } from '../lib/money'

/**
 * Recording what actually happened, and reading how far the rules have drifted.
 *
 * Drift comes first in the layout even though recording is the action, because drift is
 * the reason to bother: a rule that is reliably $23 light every fortnight is a number worth
 * correcting, and you only learn that by having recorded a few.
 */
export function Reconciliation() {
  const queryClient = useQueryClient()
  const [openFor, setOpenFor] = useState<string | null>(null)
  const [actualDate, setActualDate] = useState('')
  const [actualAmount, setActualAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const view = useGetReconciliation()

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetReconciliationQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetProjectionQueryKey().slice(0, 1) }),
    ])
  }
  const record = useRecordOccurrence({ mutation: { onSuccess: refresh } })
  const undo = useDeleteRecording({ mutation: { onSuccess: refresh } })

  const data = view.data?.status === 200 ? view.data.data : undefined
  const busy = record.isPending || undo.isPending

  if (view.isPending) return <section className="panel"><p className="movements-empty">Loading…</p></section>
  if (!data) return null

  const pending = showAll ? data.pending : data.pending.slice(0, 6)

  function openRecord(item: PendingOccurrence) {
    setError(null)
    setOpenFor(key(item))
    // Prefilled with what the rule predicted: most of the time it is right, and the point
    // is to make confirming cheap and correcting possible.
    setActualDate(item.scheduledDate)
    setActualAmount(String(toMajor(item.amountMinor, item.currency)))
  }

  async function submit(item: PendingOccurrence) {
    const amountMinor = toMinor(actualAmount, item.currency)
    if (amountMinor === null || amountMinor < 0) {
      setError('Amount must be a non-negative number.')
      return
    }
    const response = await record.mutateAsync({
      data: {
        kind: 'REALIZED',
        sourceId: item.sourceId,
        accountId: item.accountId,
        date: actualDate,
        amountMinor,
        currency: item.currency,
        direction: item.direction,
        scheduledDate: item.scheduledDate,
      },
    })
    if (response.status === 201) {
      setOpenFor(null)
      return
    }
    setError('The server rejected that.')
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Reconciliation</h2>
        <span className="panel-note">
          {data.pending.length} awaiting · {data.recordings.length} recorded
          {data.unresolved > 0 && ` · ${data.unresolved} orphaned`}
        </span>
      </div>

      {data.bySource.length > 0 && (
        <div className="drift-block">
          {data.bySource.map((s) => (
            <DriftRow key={s.sourceId} drift={s} />
          ))}
        </div>
      )}

      {data.pending.length === 0 ? (
        <p className="movements-empty">Nothing has come due that isn't already recorded.</p>
      ) : (
        <ul className="pending-list">
          {pending.map((item) => {
            const isOpen = openFor === key(item)
            return (
              <li key={key(item)}>
                <div className="pending-row">
                  <span className="pending-date">{formatDate(item.scheduledDate)}</span>
                  <span className="pending-name">{item.sourceName}</span>
                  <span className={`cf-amount ${item.direction === 'IN' ? 'is-in' : 'is-out'}`}>
                    {item.direction === 'IN' ? '+' : '−'}
                    {formatMoney(item.amountMinor, item.currency)}
                  </span>
                  {!isOpen && (
                    <button
                      type="button"
                      className="btn-ghost btn-small"
                      onClick={() => openRecord(item)}
                      disabled={busy}
                    >
                      Record
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div className="pending-form">
                    <label>
                      <span>Actually happened on</span>
                      <input
                        type="date"
                        value={actualDate}
                        onChange={(e) => setActualDate(e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Actual amount</span>
                      <input
                        value={actualAmount}
                        inputMode="decimal"
                        onChange={(e) => setActualAmount(e.target.value)}
                      />
                    </label>
                    <div className="cf-actions">
                      <button
                        type="button"
                        className="btn-ghost btn-small"
                        onClick={() => setOpenFor(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary btn-small"
                        onClick={() => submit(item)}
                        disabled={busy}
                      >
                        Confirm
                      </button>
                    </div>
                    {error && <p className="cf-error">{error}</p>}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {data.pending.length > 6 && (
        <button type="button" className="btn-ghost btn-small show-more" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show fewer' : `Show all ${data.pending.length}`}
        </button>
      )}

      {data.recordings.length > 0 && (
        <details className="recordings">
          <summary>{data.recordings.length} recorded</summary>
          <ul>
            {data.recordings.map((r) => (
              <li key={r.id}>
                <span className="pending-date">{formatDate(r.date)}</span>
                <span className="pending-name">{r.sourceName}</span>
                <span className="state-tag state-realized">{r.kind.toLowerCase()}</span>
                <span className="cf-amount">
                  {r.amountMinor != null && r.currency
                    ? formatMoney(r.amountMinor, r.currency)
                    : '—'}
                </span>
                <button
                  type="button"
                  className="btn-ghost btn-small"
                  onClick={() => undo.mutateAsync({ id: r.id })}
                  disabled={busy}
                >
                  Undo
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function DriftRow({ drift }: { drift: SourceDrift }) {
  const over = drift.meanDeltaMinor > 0
  const flagged = drift.amountDrifting || drift.dateDrifting
  return (
    <div className={`drift-row ${flagged ? 'is-flagged' : ''}`}>
      <span className="drift-name">{drift.sourceName}</span>
      <span className="drift-detail">
        {drift.occurrences} recorded · rule says{' '}
        {formatMoney(drift.meanExpectedMinor, drift.currency)}, actually averages{' '}
        <strong>{formatMoney(drift.meanActualMinor, drift.currency)}</strong>
      </span>
      <span className={`drift-delta ${flagged ? (over ? 'is-over' : 'is-under') : ''}`}>
        {over ? '+' : ''}
        {formatMoney(drift.meanDeltaMinor, drift.currency)}
        {drift.dateDrifting && (
          <span className="drift-days">
            {' '}
            {drift.meanDayDelta > 0 ? '+' : ''}
            {drift.meanDayDelta.toFixed(1)}d
          </span>
        )}
      </span>
    </div>
  )
}

/** Pending items have no id of their own — they are derived, not stored. */
function key(item: PendingOccurrence): string {
  return `${item.sourceId}@${item.scheduledDate}`
}
